import {css} from '@emotion/core';
import styled from '@emotion/styled';
import { FontNormal } from '@css/fonts';
import { formInputStyle, animatedStyle } from '@css/styles';
import {
  red,
  lightBlue,
  backgroundGray,
  gray,
  white,
  lochmaraBlue,
  MQ,
} from '@css/constants';
import { fontSize } from "@css/styles";

export const RadioButtonContainer = styled('div')(css`
  ${formInputStyle};

  width: 100%;
  margin: 0 auto;
  display: inline-block;
`);

export const RadioCheckmark = styled('span')(css`
  position: absolute;
  top: 12px;
  left: 12px;
  height: 30px;
  width: 30px;
  border: 1px solid ${gray};
  border-radius: 50%;

  ${animatedStyle}

  &:after {
    content: "";
    position: absolute;
    display: none;

    top: 5px;
    left: 5px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${lightBlue};
  }
`);

export const RadioOption = styled('div')<{ showBoxShadow: boolean }>(props => css`
  margin-bottom: 10px;
  position: relative;

  background-color: ${white};
  border: 1px solid ${gray};
  box-sizing: border-box;
  border-radius: 2px;
  box-shadow: ${props.showBoxShadow ? '0px 4px 12px rgba(26, 28, 31, 0.15)' : 'none'};

  &:hover {
    background-color: ${backgroundGray};
  }

  &:hover input ~ ${RadioCheckmark} {
    background-color: #f1f1f1;
  }

  input:checked ~ ${RadioCheckmark} {
    background-color: ${white};
  }

  input:checked ~ ${RadioCheckmark}:after {
    display: block;
  }
`);

export const radioOptionSelectedStyle = css`
  background-color: ${backgroundGray}
`;

export const RadioLabelWrapper = styled('div')(css`
  ${FontNormal(600)};

  display: block;
  padding-left: 60px;
  padding: 19px 10px;

  cursor: pointer;
  text-align: left;

  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
`);

export const RadioInput = styled('input')(css`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
`);

export const RadioLabel = styled('label')(css`
  ${FontNormal(500)};
  display: block;
  text-align: center;
`);

export const RadioNote = styled('div')(css`
  ${FontNormal(400)};
  ${fontSize(14)};
  color: ${lochmaraBlue};
  margin-bottom: -8px;
`);

export const radioLabelErrorStyle = css`
  color: ${red};
`;

export const RadioOptionContainer = styled('div')(css``);

export const RadioOptionGenderWrapper = styled('div')(css`
  width: 140px;
  margin: 0 auto;
`);

interface RadioOptionGenderIconProps {
  image: string;
}
export const RadioOptionGenderIcon = styled('div')<RadioOptionGenderIconProps>((props) => css`
  margin: 20px auto 10px;
  width: 80px;
  height: 80px;
  background-image: url("${props.image}");
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
`);

export const RadioGenderCheckmark = styled(RadioCheckmark)(css`
  position: relative;
  top: initial;
  right: initial;
  bottom: 0;
  left: 0;
  height: 24px;
  width: 24px;
  margin: 5px auto 15px;
  display: block;

   &:after {
    content: "";
    position: absolute;
    display: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${lightBlue};
  }
`);

export const RadioGenderOptionContainer = styled(RadioOptionContainer)((props: any) => css`
  float: ${props.position};
`);

export const RadioGenderOption = styled(RadioOption)<{ isSelected?: boolean }>(props => css`
  border-radius: 2px;
  background-color: ${props.isSelected ? backgroundGray : white};

  &:hover {
    background-color: ${backgroundGray};
  }

  &:hover input ~ ${RadioGenderCheckmark} {
    background-color: #f1f1f1;
  }

  input:checked ~ ${RadioGenderCheckmark} {
    background-color: ${white};
  }

  input:checked ~ ${RadioGenderCheckmark}:after {
    display: block;
  }
`);

export const RadioGenderLabel = styled(RadioLabel)(css`
  ${FontNormal(500)};
  ${fontSize(18)};
  text-transform: uppercase;
`);

export const RadioGenderInput = styled(RadioInput)(css`
  top: 0;
  left: 0;
  height: 100%;
`);

export const FatMode = styled('div')(css`
  ${MQ.SMALL_SCREENS} {
    display: flex;
    justify-content: center;
  }

  ${RadioGenderOptionContainer} {
    flex: none;
    flex-grow: 1;
    width: calc(50% - 1px);
    max-width: 150px;
  }

  ${RadioGenderOptionContainer}:first-of-type {
    margin-right: 8px;
  }

  ${RadioOptionGenderWrapper} {
    width: auto;
  }
`);

export const SlimMode = styled('div')<{ hasError?: boolean }>(props => css`
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${gray};
  border-radius: 2px;
  ${props.hasError && `border: 3px solid ${red};`}

  ${RadioOptionGenderWrapper} {
    display: none;
  }

  ${RadioGenderLabel} {
    display: inline-block;
    text-transform: capitalize;
    position: relative;
    top: 2px;
  }

  ${RadioGenderOptionContainer} {
    flex: none;
    flex-grow: 1;
    width: calc(50% - 1px);
  }

  ${RadioGenderOption} {
    border-color: transparent;
    border-radius: 0;
    padding: 3px 12px;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    justify-content: flex-end;
    flex-grow: 0;
    ${props.hasError && (`
      border: none;
      padding-top: 1px;
      padding-bottom: 2px;
    `)}
  }

  ${RadioGenderOptionContainer}:first-of-type {
    border-right: 1px solid ${gray};
  }

  ${RadioGenderCheckmark} {
    margin: 12px 4px 10px 4px;
  }
`);

export const SlimRBWrapper = styled('div')(css`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;

  ${RadioOption} {
    flex-grow: 1;
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 59px;
    margin-bottom: -1px;
  }

  ${RadioOption}:first-of-type {
    border-left: 1px solid ${gray};
    border-radius: 2px;
    border-right: 0;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }

  ${RadioCheckmark} {
    position: relative;
    top: -2px;
    margin-right: 10px;
    flex-shrink: 0;
  }

  ${RadioLabelWrapper} {
    padding: 0 10px;
  }

  ${RadioLabel} {
    overflow-wrap: break-word;
  }
`);
